import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { BsFire, BsExclamationTriangle, BsClock } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';

import { formatEuro } from '../../../utils/formatters';

// Collapse the tag list to this many chips; the rest hide behind a "show more" toggle.
const TAG_COLLAPSED_LIMIT = 8;
const VALUE_MAX = 1000000;
const VALUE_STEP = 5000;

const rangeDraftOf = (filters) => ({
	valueMin: filters.valueMin ?? 0,
	valueMax: filters.valueMax ?? VALUE_MAX,
	probMin: filters.probMin ?? 0,
});

// Sliders fire on every pixel; a URL write per event would flood the history, so they edit a
// local draft that is committed when the thumb is released.
const rangeCommitHandlers = (commit) => ({ onPointerUp: commit, onKeyUp: commit, onTouchEnd: commit });

const PipelineFilters = ({ filters, onChange, members, tags = [], activeFilterCount, onClose, onReset, anchorRef }) => {
	const [tagsExpanded, setTagsExpanded] = useState(false);
	const [rangeDraft, setRangeDraft] = useState(() => rangeDraftOf(filters));
	const owners = filters.userIds ?? [];
	const tagIds = filters.tagIds ?? [];

	useEffect(() => {
		setRangeDraft(
			rangeDraftOf({ valueMin: filters.valueMin, valueMax: filters.valueMax, probMin: filters.probMin }),
		);
	}, [filters.valueMin, filters.valueMax, filters.probMin]);

	const commitRange = () => {
		const next = {
			valueMin: rangeDraft.valueMin || undefined,
			valueMax: rangeDraft.valueMax >= VALUE_MAX ? undefined : rangeDraft.valueMax,
			probMin: rangeDraft.probMin || undefined,
		};
		const unchanged = Object.entries(next).every(([key, value]) => filters[key] === value);
		if (!unchanged) onChange(next);
	};

	const toggleIn = (list, value) => (list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

	const toggleOwner = (userId) => {
		const next = toggleIn(owners, userId);
		onChange({ userIds: next.length > 0 ? next : undefined });
	};

	const toggleTag = (tagId) => {
		const next = toggleIn(tagIds, tagId);
		onChange({
			tagIds: next.length > 0 ? next : undefined,
			...(next.length < 2 ? { tagMatch: undefined } : {}),
		});
	};

	// Collapsed view keeps selected tags visible (so the active filter is never hidden) and
	// fills the remaining slots with unselected tags up to the limit.
	const selectedTags = tags.filter((t) => tagIds.includes(t.id));
	const unselectedTags = tags.filter((t) => !tagIds.includes(t.id));
	const visibleTags =
		tagsExpanded || tags.length <= TAG_COLLAPSED_LIMIT
			? tags
			: [...selectedTags, ...unselectedTags].slice(0, Math.max(TAG_COLLAPSED_LIMIT, selectedTags.length));
	const hiddenCount = tags.length - visibleTags.length;

	const resetAction = activeFilterCount > 0 && (
		<button type="button" onClick={onReset} className="text-xs text-bgBase-800 hover:text-text">
			Zurücksetzen
		</button>
	);

	return (
		<Popover open onClose={onClose} anchorRef={anchorRef} align="start">
			<Panel.Header actions={resetAction}>Filter</Panel.Header>
			<Panel.Body className="space-y-3.5">
				<div className="space-y-1.5">
					<label className="text-xs-head text-bgBase-800">Deal-Besitzer</label>
					<div className="flex flex-wrap gap-1.5">
						{members.map((m) => (
							<button
								key={m.userId}
								type="button"
								onClick={() => toggleOwner(m.userId)}
								className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${
									owners.includes(m.userId)
										? 'bg-primary-600 text-white border-primary-600'
										: 'bg-white text-bgBase-800 border-bgBase-400 hover:border-primary-300 hover:text-text'
								}`}
							>
								<span className="w-4 h-4 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[8px] font-bold flex-shrink-0">
									{(m.fullName ?? m.email ?? '?')[0]?.toUpperCase()}
								</span>
								{m.fullName ?? m.email}
							</button>
						))}
					</div>
				</div>

				{tags.length > 0 && (
					<div className="space-y-1.5">
						<div className="flex items-center justify-between">
							<label className="text-xs-head text-bgBase-800">Tags</label>
							{/* AND/OR toggle only matters once 2+ tags are picked. */}
							{tagIds.length > 1 && (
								<div className="flex bg-bgBase-300 border border-bgBase-500 rounded-control p-0.5 gap-0.5">
									{[
										{ value: 'any', label: 'Eines' },
										{ value: 'all', label: 'Alle' },
									].map((opt) => (
										<button
											key={opt.value}
											type="button"
											onClick={() =>
												onChange({ tagMatch: opt.value === 'any' ? undefined : opt.value })
											}
											className={`px-1.5 py-0.5 rounded-control text-[10px] font-medium transition-colors ${
												(filters.tagMatch ?? 'any') === opt.value
													? 'bg-white text-primary-700'
													: 'text-bgBase-800 hover:text-text'
											}`}
										>
											{opt.label}
										</button>
									))}
								</div>
							)}
						</div>
						<div className="flex flex-wrap gap-1.5">
							{visibleTags.map((tag) => (
								<button
									key={tag.id}
									type="button"
									onClick={() => toggleTag(tag.id)}
									className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${
										tagIds.includes(tag.id)
											? 'bg-primary-600 text-white border-primary-600'
											: 'bg-white text-bgBase-800 border-bgBase-400 hover:border-primary-300 hover:text-text'
									}`}
								>
									{tag.name}
								</button>
							))}
							{(hiddenCount > 0 || tagsExpanded) && tags.length > TAG_COLLAPSED_LIMIT && (
								<button
									type="button"
									onClick={() => setTagsExpanded((v) => !v)}
									className="inline-flex items-center px-2.5 py-1 text-[11px] font-medium text-primary-600 hover:text-primary-700"
								>
									{tagsExpanded ? 'Weniger anzeigen' : `+${hiddenCount} weitere anzeigen`}
								</button>
							)}
						</div>
					</div>
				)}

				<div className="space-y-1.5">
					<div className="flex items-center justify-between">
						<label className="text-xs-head text-bgBase-800">Dealwert</label>
						<span className="text-[10px] text-bgBase-800 tabular-nums">
							{formatEuro(rangeDraft.valueMin, '0 €')} –{' '}
							{rangeDraft.valueMax < VALUE_MAX ? formatEuro(rangeDraft.valueMax) : '∞'}
						</span>
					</div>
					<div className="space-y-1.5">
						<input
							type="range"
							min="0"
							max={VALUE_MAX}
							step={VALUE_STEP}
							value={rangeDraft.valueMin}
							onChange={(e) => {
								const v = Number(e.target.value);
								setRangeDraft((d) => ({
									...d,
									valueMin: v === 0 ? 0 : Math.min(v, d.valueMax - VALUE_STEP),
								}));
							}}
							{...rangeCommitHandlers(commitRange)}
							className="w-full accent-primary-500 h-1"
						/>
						<input
							type="range"
							min="0"
							max={VALUE_MAX}
							step={VALUE_STEP}
							value={rangeDraft.valueMax}
							onChange={(e) => {
								const v = Number(e.target.value);
								setRangeDraft((d) => ({
									...d,
									valueMax: v >= VALUE_MAX ? VALUE_MAX : Math.max(v, d.valueMin + VALUE_STEP),
								}));
							}}
							{...rangeCommitHandlers(commitRange)}
							className="w-full accent-primary-500 h-1"
						/>
					</div>
				</div>

				<div className="space-y-1.5">
					<div className="flex items-center justify-between">
						<label className="text-xs-head text-bgBase-800">Mindest-Wahrscheinlichkeit</label>
						<span className="text-[10px] text-bgBase-800 tabular-nums">{rangeDraft.probMin}%</span>
					</div>
					<input
						type="range"
						min="0"
						max="100"
						step="5"
						value={rangeDraft.probMin}
						onChange={(e) => setRangeDraft((d) => ({ ...d, probMin: Number(e.target.value) }))}
						{...rangeCommitHandlers(commitRange)}
						className="w-full accent-primary-500 h-1"
					/>
				</div>

				<div className="space-y-2">
					<Checkbox
						id="pipeline-filter-hot-deals"
						checked={Boolean(filters.hotDealsOnly)}
						onChange={(checked) => onChange({ hotDealsOnly: checked || undefined })}
						label={
							<span className="inline-flex items-center gap-1.5">
								<BsFire className="w-3 h-3 text-caution-500" /> Nur heiße Deals
							</span>
						}
					/>
					<Checkbox
						id="pipeline-filter-overdue-activities"
						checked={Boolean(filters.overdueActivitiesOnly)}
						onChange={(checked) => onChange({ overdueActivitiesOnly: checked || undefined })}
						label={
							<span className="inline-flex items-center gap-1.5">
								<BsExclamationTriangle className="w-3 h-3 text-warning-500" /> Nur mit überfälligen
								Aktivitäten
							</span>
						}
					/>
					<Checkbox
						id="pipeline-filter-inactive-deals"
						checked={Boolean(filters.inactiveDealsOnly)}
						onChange={(checked) => onChange({ inactiveDealsOnly: checked || undefined })}
						label={
							<span className="inline-flex items-center gap-1.5">
								<BsClock className="w-3 h-3 text-caution-500" /> Inaktive Deals (keine Aktivität seit 3
								Monaten)
							</span>
						}
					/>
				</div>
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button onClick={onClose} size="sm">
					Übernehmen
				</Button>
			</Panel.Footer>
		</Popover>
	);
};

PipelineFilters.propTypes = {
	/** Parsed URL filters (see buildDealFilterSchema). */
	filters: PropTypes.object.isRequired,
	/** Receives a partial filter update; undefined removes a key. */
	onChange: PropTypes.func.isRequired,
	members: PropTypes.array.isRequired,
	tags: PropTypes.array,
	activeFilterCount: PropTypes.number.isRequired,
	onClose: PropTypes.func.isRequired,
	onReset: PropTypes.func.isRequired,
	anchorRef: PropTypes.shape({ current: PropTypes.any }),
};

export default PipelineFilters;
